import type { TersaImageModel } from '../types';
import { providers } from '../../providers';
import { wavespeed } from '../wavespeed';
import { kie } from '../kie';

// 系数
const COST_FACTOR = 1.5;

export const recraftModels: Record<string, TersaImageModel> = {
    'recraft-remove-background': {
        chef: providers.recraft,
        hideInstructions: true,
        tags: ['image-remove-background'],
        providers: [
            {
                ...providers.recraft,
                model: kie.image('recraft-remove-background'),
                modelBackup: [wavespeed.image('ideogram-ai/remove-background')],
                getCost: () => {
                    return 0.01 * COST_FACTOR;
                },
            },
        ],
        configByMode: {
            i2i: {
                sizes: [],
                resolutions: [],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                },
            },
        },
        precheckByMode: {
            i2i: ['image'],
        },
        imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        supportsInput: ['i2i'],
        supportsEdit: true,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    'recraft-crisp-upscale': {
        chef: providers.recraft,
        providers: [
            {
                ...providers.recraft,
                model: wavespeed.image('recraft-ai/recraft-crisp-upscale'),
                getCost: () => {
                    return 0.004 * COST_FACTOR;
                },
            },
        ],
        hideInstructions: true,
        tags: ['image-upscale'],
        configByMode: {
            i2i: {
                sizes: [],
                resolutions: [],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                },
            },
        },
        precheckByMode: {
            i2i: ['image'],
        },
        imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        supportsInput: ['i2i'],
        supportsEdit: true,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    'recraft-creative-upscale': {
        chef: providers.recraft,
        providers: [
            {
                ...providers.recraft,
                model: wavespeed.image('recraft-ai/recraft-creative-upscale'),
                getCost: () => {
                    return 0.25 * COST_FACTOR;
                },
            },
        ],
        hideInstructions: true,
        tags: ['image-upscale'],
        configByMode: {
            i2i: {
                sizes: [],
                resolutions: [],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                },
            },
        },
        precheckByMode: {
            i2i: ['image'],
        },
        imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        supportsInput: ['i2i'],
        supportsEdit: true,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    'recraft-v4.1/text-to-vector': {
        chef: providers.recraft,
        providers: [
            {
                ...providers.recraft,
                model: wavespeed.image('recraft-v4.1/text-to-vector'),
                getCost: () => {
                    return 0.08 * COST_FACTOR;
                },
            },
        ],
        configByMode: {
            t2i: {
                sizes: ['16x9', '9x16', '4x3', '3x4', '1x1'],
                resolutions: [],
            },
        },
        precheckByMode: {
            t2i: ['prompt'],
        },
        imagesCountOptions: [],
        supportsInput: ['t2i'],
        supportsEdit: false,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    'recraft-v4.1-pro/text-to-vector': {
        chef: providers.recraft,
        providers: [
            {
                ...providers.recraft,
                model: wavespeed.image('recraft-v4.1-pro/text-to-vector'),
                getCost: () => {
                    return 0.30 * COST_FACTOR;
                },
            },
        ],
        configByMode: {
            t2i: {
                sizes: ['16x9', '9x16', '4x3', '3x4', '1x1'],
                resolutions: [],
            },
        },
        precheckByMode: {
            t2i: ['prompt'],
        },
        imagesCountOptions: [],
        supportsInput: ['t2i'],
        supportsEdit: false,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    'recraft-v4.1/text-to-image': {
        chef: providers.recraft,
        providers: [
            {
                ...providers.recraft,
                model: wavespeed.image('recraft-v4.1/text-to-image'),
                getCost: () => {
                    return 0.04 * COST_FACTOR;
                },
            },
        ],
        configByMode: {
            t2i: {
                sizes: ['1x1', '16x9', '9x16', '4x3', '3x4'],
                resolutions: [],
            },
        },
        precheckByMode: {
            t2i: ['prompt'],
        },
        imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        supportsInput: ['t2i'],
        supportsEdit: false,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    'recraft-v4.1-flash/text-to-image': {
        chef: providers.recraft,
        providers: [
            {
                ...providers.recraft,
                model: wavespeed.image('recraft-v4.1-flash/text-to-image'),
                getCost: () => {
                    return 0.008 * COST_FACTOR;
                },
            },
        ],
        configByMode: {
            t2i: {
                sizes: ['1x1', '16x9', '9x16', '4x3', '3x4'],
                resolutions: [],
            },
        },
        precheckByMode: {
            t2i: ['prompt'],
        },
        imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        supportsInput: ['t2i'],
        supportsEdit: false,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    'recraft-v4.1-pro/text-to-image': {
        chef: providers.recraft,
        providers: [
            {
                ...providers.recraft,
                model: wavespeed.image('recraft-v4.1-pro/text-to-image'),
                getCost: () => {
                    return 0.25 * COST_FACTOR;
                },
            },
        ],
        configByMode: {
            t2i: {
                sizes: ['1x1', '16x9', '9x16', '4x3', '3x4'],
                resolutions: [],
            },
        },
        precheckByMode: {
            t2i: ['prompt'],
        },
        imagesCountOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        supportsInput: ['t2i'],
        supportsEdit: false,
        supportsImageType: 'url',
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
};